import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
  Fade,
  Zoom,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DownloadIcon from "@mui/icons-material/Download";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import PaymentIcon from "@mui/icons-material/Payment";
import HotelIcon from "@mui/icons-material/Hotel";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import useCustomToast from "../hooks/useCustomToast";
import CryptoJS from "crypto-js";
import Cookies from "js-cookie";

const SECRET_KEY = "tabtrips_hotel_secret";
const TOKEN_COOKIE_KEY = "token";

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

const StyledTableCell = ({ children, ...props }) => (
  <TableCell
    {...props}
    sx={{
      fontWeight: 600,
      fontSize: { xs: "0.75rem", sm: "0.875rem" },
      color: "#2E4057",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      py: { xs: 1, sm: 1.5 },
      ...props.sx,
    }}
  >
    {children}
  </TableCell>
);

const MotionTableRow = motion.create(TableRow);

const StyledTableRow = ({ children, onClick, ...props }) => (
  <MotionTableRow
    {...props}
    onClick={onClick}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    sx={{
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.2s ease",
      "&:hover": onClick
        ? {
            backgroundColor: "rgba(255, 119, 0, 0.05)",
            transform: "translateX(4px)",
          }
        : {},
      "&:last-child td": { border: 0 },
      ...props.sx,
    }}
  >
    {children}
  </MotionTableRow>
);

const SettlementsPage = () => {
  const [loading, setLoading] = useState(true);
  const [settlements, setSettlements] = useState([]);
  const [filteredSettlements, setFilteredSettlements] = useState([]);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [downloadingVoucher, setDownloadingVoucher] = useState(false);
  const [hotelName, setHotelName] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [totalSettlements, setTotalSettlements] = useState(0);
  const [commissionExpanded, setCommissionExpanded] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { successToast, errorToast } = useCustomToast();

  useEffect(() => {
    fetchSettlements();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = settlements.filter(
        (settlement) =>
          settlement.booking_code
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          settlement.guest_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          settlement.razorpay_transfer_id
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
      setFilteredSettlements(filtered);
    } else {
      setFilteredSettlements(settlements);
    }
  }, [searchTerm, settlements]);

  const fetchSettlements = async () => {
    try {
      setLoading(true);
      const token = getDecryptedToken();
      const hotelId = localStorage.getItem("selectedHotelId");

      if (!token || !hotelId) {
        errorToast("Please log in and select a property");
        setLoading(false);
        return;
      }

      const response = await axios.post(
        "https://api.tabtrips.com/olivrweb/hotelapi/tabtrips_S.php?action=getHotelPaymentSettlements",
        {
          token: token,
          hotel_id: parseInt(hotelId),
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status) {
        setSettlements(response.data.data || []);
        setFilteredSettlements(response.data.data || []);
        setHotelName(response.data.hotel_name || "");
        setCurrency(response.data.currency || "INR");
        setTotalSettlements(response.data.total_settlements || 0);
      } else {
        errorToast(response.data.message || "Failed to fetch settlements");
        setSettlements([]);
        setFilteredSettlements([]);
      }
    } catch (error) {
      console.error("Error fetching settlements:", error);
      errorToast("An error occurred while fetching settlements");
      setSettlements([]);
      setFilteredSettlements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSettlementClick = (settlement) => {
    setSelectedSettlement(settlement);
    setDetailDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDetailDialogOpen(false);
    setSelectedSettlement(null);
  };

  const downloadVoucher = async (bookingId) => {
    try {
      setDownloadingVoucher(true);
      const token = getDecryptedToken();

      const response = await axios.get(
        `https://api.tabtrips.com/olivrweb/hotelapi/tabtrips_S.php?action=generateVoucher&booking_id=${bookingId}&voucher_type=hotelier`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          responseType: "json",
        }
      );

      if (response.data.status && response.data.pdf_base64) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = response.data.file_name || `voucher_${bookingId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        successToast("Voucher downloaded successfully");
      } else {
        errorToast(response.data.message || "Failed to download voucher");
      }
    } catch (error) {
      console.error("Error downloading voucher:", error);
      errorToast("Failed to download voucher");
    } finally {
      setDownloadingVoucher(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  /** Date only, no weekday - e.g. "Mar 14, 2026" for settlement dialog */
  const formatDateShort = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  useEffect(() => {
    setCommissionExpanded(false);
  }, [selectedSettlement?.settlement_id]);

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "transferred":
        return "success";
      case "captured":
        return "info";
      case "failed":
        return "error";
      case "refunded":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "transferred":
        return <CheckCircleIcon fontSize="small" />;
      case "captured":
        return <PendingIcon fontSize="small" />;
      case "failed":
        return <CancelIcon fontSize="small" />;
      default:
        return <PendingIcon fontSize="small" />;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: "#FF7700" }} size={60} />
        <Typography variant="body2" color="text.secondary">
          Loading settlements...
        </Typography>
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: { xs: 2, sm: 3 },
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => navigate("/reservations")}
              sx={{
                color: "#FF7700",
                border: "1.5px solid #FF7700",
                background: "#fff",
                "&:hover": {
                  background: "rgba(255,119,0,0.08)",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: "#2E4057",
                  fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                }}
              >
                Payment Settlements
              </Typography>
              {hotelName && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {hotelName}
                </Typography>
              )}
            </Box>
          </Box>
          {totalSettlements > 0 && (
            <Chip
              icon={<AccountBalanceWalletIcon />}
              label={`${totalSettlements} Settlement${totalSettlements !== 1 ? "s" : ""}`}
              sx={{
                backgroundColor: "#FF7700",
                color: "#fff",
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            />
          )}
        </Box>

        {/* Search Bar */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            mb: 3,
            p: { xs: 1.5, sm: 2 },
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <TextField
            fullWidth
            placeholder="Search by booking code, guest name, or transfer ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#FF7700" }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    sx={{ color: "#999" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
          />
        </Paper>

        {/* Settlements Table */}
        {filteredSettlements.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              textAlign: "center",
              py: 8,
              borderRadius: "14px",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <AccountBalanceWalletIcon
              sx={{ fontSize: 64, color: "#ccc", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No settlements found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm
                ? "Try adjusting your search criteria"
                : "There are no payment settlements available for this property."}
            </Typography>
          </Paper>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: "14px",
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <StyledTableCell>Booking Code</StyledTableCell>
                  <StyledTableCell>Guest Name</StyledTableCell>
                  <StyledTableCell>Check-in / Check-out</StyledTableCell>
                  <StyledTableCell>Nights</StyledTableCell>
                  <StyledTableCell align="right">Gross Amount</StyledTableCell>
                  <StyledTableCell align="right">Commission</StyledTableCell>
                  <StyledTableCell align="right">Hotel Amount</StyledTableCell>
                  <StyledTableCell>Settlement Status</StyledTableCell>
                  {!isMobile && <StyledTableCell>Transfer ID</StyledTableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                <AnimatePresence>
                  {filteredSettlements.map((settlement, index) => (
                    <StyledTableRow
                      key={settlement.settlement_id}
                      onClick={() => handleSettlementClick(settlement)}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <StyledTableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "#FF7700",
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          }}
                        >
                          {settlement.booking_code}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <PersonIcon sx={{ fontSize: 16, color: "#999" }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            }}
                          >
                            {settlement.guest_name}
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            }}
                          >
                            {formatDate(settlement.check_in_date)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                              color: "text.secondary",
                            }}
                          >
                            {formatDate(settlement.check_out_date)}
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          label={settlement.total_nights}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255,119,0,0.1)",
                            color: "#FF7700",
                            fontWeight: 600,
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          }}
                        >
                          {currency} {parseFloat(settlement.gross_amount).toFixed(2)}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            color: "error.main",
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          }}
                        >
                          -{currency} {parseFloat(settlement.commission_amount).toFixed(2)}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: "#2E4057",
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          }}
                        >
                          {currency} {parseFloat(settlement.hotel_amount).toFixed(2)}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          icon={getStatusIcon(settlement.settlement_status)}
                          label={settlement.settlement_status || "Pending"}
                          color={getStatusColor(settlement.settlement_status)}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                        />
                      </StyledTableCell>
                      {!isMobile && (
                        <StyledTableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.75rem",
                              color: "text.secondary",
                              fontFamily: "monospace",
                            }}
                          >
                            {settlement.razorpay_transfer_id || "N/A"}
                          </Typography>
                        </StyledTableCell>
                      )}
                    </StyledTableRow>
                  ))}
                </AnimatePresence>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 300 }}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 2,
              maxHeight: isMobile ? "100vh" : "90vh",
              overflow: "hidden",
            },
          }}
        >
          {selectedSettlement && (
            <>
              <DialogTitle
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  pb: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Settlement Details
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {selectedSettlement.booking_code}
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseDialog} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ pt: { xs: 2, sm: 2.5 }, px: { xs: 1.5, sm: 3 } }}>
                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                  {/* Guest Information - compact */}
                  <Grid item xs={12} sm={6}>
                    <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, height: "100%", minHeight: 0 }}>
                      <CardContent sx={{ py: 0.75, px: 1.25, "&:last-child": { pb: 0.75 } }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <PersonIcon sx={{ color: "#FF7700", fontSize: 18 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Guest Information</Typography>
                        </Box>
                        <Stack spacing={0.4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Guest Name</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.15, fontSize: "0.8rem", lineHeight: 1.35 }}>{selectedSettlement.guest_name}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Total Guests</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.15, fontSize: "0.8rem", lineHeight: 1.35 }}>{selectedSettlement.total_guests}</Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Booking Dates - compact */}
                  <Grid item xs={12} sm={6}>
                    <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, height: "100%", minHeight: 0 }}>
                      <CardContent sx={{ py: 0.75, px: 1.25, "&:last-child": { pb: 0.75 } }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <CalendarTodayIcon sx={{ color: "#FF7700", fontSize: 18 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Booking Dates</Typography>
                        </Box>
                        <Stack spacing={0.4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Check-in</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.15, fontSize: "0.8rem", lineHeight: 1.35 }}>{formatDateShort(selectedSettlement.check_in_date)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Check-out</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.15, fontSize: "0.8rem", lineHeight: 1.35 }}>{formatDateShort(selectedSettlement.check_out_date)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Total Nights</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.15, fontSize: "0.8rem", lineHeight: 1.35 }}>{selectedSettlement.total_nights}</Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Settlement Information - compact, same row as Payment on md+ */}
                  <Grid item xs={12} md={6}>
                    <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, height: "100%", minHeight: 0 }}>
                      <CardContent sx={{ py: 0.75, px: 1.25, "&:last-child": { pb: 0.75 } }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <TrendingUpIcon sx={{ color: "#FF7700", fontSize: 18 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Settlement Information</Typography>
                        </Box>
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Settlement Status</Typography>
                            <Box sx={{ mt: 0.35 }}>
                              <Chip
                                icon={getStatusIcon(selectedSettlement.settlement_status)}
                                label={selectedSettlement.settlement_status || "Pending"}
                                color={getStatusColor(selectedSettlement.settlement_status)}
                                size="small"
                                sx={{ fontWeight: 600, height: 24, "& .MuiChip-label": { px: 0.75, fontSize: "0.75rem" } }}
                              />
                            </Box>
                          </Grid>
                          {selectedSettlement.razorpay_transfer_id && (
                            <Grid item xs={12} sm={6} md={3}>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Transfer ID</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.15, fontSize: "0.8rem", fontFamily: "monospace", lineHeight: 1.35 }}>{selectedSettlement.razorpay_transfer_id}</Typography>
                            </Grid>
                          )}
                          {selectedSettlement.transfer_date && (
                            <Grid item xs={12} sm={6} md={3}>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Transfer Date</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.15, fontSize: "0.8rem", lineHeight: 1.35 }}>{formatDateTime(selectedSettlement.transfer_date)}</Typography>
                            </Grid>
                          )}
                          {selectedSettlement.checkout_date && (
                            <Grid item xs={12} sm={6} md={3}>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Actual Checkout</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.15, fontSize: "0.8rem", lineHeight: 1.35 }}>{formatDateShort(selectedSettlement.checkout_date)} at {selectedSettlement.checkout_time || "N/A"}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Payment Information - compact, same row as Settlement on md+ */}
                  <Grid item xs={12} md={6}>
                    <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, height: "100%", minHeight: 0 }}>
                      <CardContent sx={{ py: 0.75, px: 1.25, "&:last-child": { pb: 0.75 } }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <PaymentIcon sx={{ color: "#FF7700", fontSize: 18 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Payment Information</Typography>
                        </Box>
                        <Stack direction="column" spacing={0.5}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Payment Status</Typography>
                            <Box sx={{ mt: 0.35 }}>
                              <Chip
                                label={selectedSettlement.payment_status || "—"}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  height: 24,
                                  "& .MuiChip-label": { px: 0.75, fontSize: "0.75rem" },
                                  ...((selectedSettlement.payment_status || "").toLowerCase() === "paid"
                                    ? { bgcolor: "#22c55e", color: "#fff", "&:hover": { bgcolor: "#16a34a" } }
                                    : {}),
                                }}
                              />
                            </Box>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Payment Method</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.15, fontSize: "0.8rem", lineHeight: 1.35 }}>{(selectedSettlement.payment_method || "").toLowerCase() === "razorpay" ? "TabTrips" : selectedSettlement.payment_method || "—"}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Payment Type</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.15, fontSize: "0.8rem", lineHeight: 1.35 }}>{selectedSettlement.payment_type_display}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", lineHeight: 1.2 }}>Booked On</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.15, fontSize: "0.8rem", lineHeight: 1.35 }}>{formatDateTime(selectedSettlement.booked_on)}</Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Rate Breakup - replaces orange box */}
                  <Grid item xs={12}>
                    <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Rate Breakup</Typography>
                        <Stack divider={<Divider />} spacing={1.5}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>(A) Hotel Gross Charges</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Room charges, extra occupancies, hotel taxes</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{currency} {parseFloat(selectedSettlement.discounted_amount || 0).toFixed(2)}</Typography>
                          </Box>
                          <Box>
                            <Box
                              sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1, cursor: "pointer" }}
                              onClick={() => setCommissionExpanded((e) => !e)}
                            >
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>(B) Commission</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>including GST</Typography>
                                </Box>
                                {commissionExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{currency} {parseFloat(selectedSettlement.commission_amount || 0).toFixed(2)}</Typography>
                            </Box>
                            <Collapse in={commissionExpanded}>
                              <Stack spacing={0.5} sx={{ mt: 1, pl: 2, borderLeft: "2px solid", borderColor: "divider" }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                  <Typography variant="body2" color="text.secondary">Commission</Typography>
                                  <Typography variant="body2">{currency} {parseFloat(selectedSettlement.tabtrips_commission || 0).toFixed(2)}</Typography>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                  <Typography variant="body2" color="text.secondary">GST @ 18% (includes IGST/CGST/SGST)</Typography>
                                  <Typography variant="body2">{currency} {parseFloat(selectedSettlement.gst_on_tabtrips_commission || 0).toFixed(2)}</Typography>
                                </Box>
                              </Stack>
                            </Collapse>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>(C) Tax Deductions</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{currency} {parseFloat(selectedSettlement.tax_deductions || 0).toFixed(2)}</Typography>
                          </Box>
                          {/* Payment gateway charge hidden for now
                          {selectedSettlement.payment_gateway_charge != null &&
                            selectedSettlement.payment_gateway_charge !== "" &&
                            Number(selectedSettlement.payment_gateway_charge) > 0 && (
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>(D) Payment gateway charge</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                                  {currency} {parseFloat(selectedSettlement.payment_gateway_charge || 0).toFixed(2)}
                                </Typography>
                              </Box>
                            )}
                          */}
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, pt: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Payable to Hotel (A-B-C)
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, whiteSpace: "nowrap", color: "#FF7700" }}>{currency} {parseFloat(selectedSettlement.hotel_amount || 0).toFixed(2)}</Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions
                sx={{
                  px: 3,
                  pb: 3,
                  pt: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Button
                  onClick={handleCloseDialog}
                  sx={{
                    color: "text.secondary",
                    textTransform: "none",
                  }}
                >
                  Close
                </Button>
                {/* <Button
                  variant="contained"
                  startIcon={
                    downloadingVoucher ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={() => downloadVoucher(selectedSettlement.booking_id)}
                  disabled={downloadingVoucher}
                  sx={{
                    backgroundColor: "#FF7700",
                    color: "#fff",
                    textTransform: "none",
                    px: 3,
                    "&:hover": {
                      backgroundColor: "#ff8800",
                    },
                  }}
                >
                  {downloadingVoucher ? "Downloading..." : "Download Voucher"}
                </Button> */}
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </motion.div>
  );
};

export default SettlementsPage;

